import { useState, useCallback, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, MemoryRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { brandConfig } from "@/lib/brandConfig";
import { WidgetModeProvider, useWidgetMode } from "@/lib/widgetMode";
import { ShadowContainerProvider } from "@/lib/shadowRoot";

// Screens
import VINEntryScreen from "./screens/VINEntryScreen";
import VehicleConfirmScreen from "./screens/VehicleConfirmScreen";
import MileageZipScreen from "./screens/MileageZipScreen";
import InstantOfferScreen from "./screens/InstantOfferScreen";
import ConditionScreen from "./screens/ConditionScreen";
import PhotoUploadScreen from "./screens/PhotoUploadScreen";
import LockedOfferScreen from "./screens/LockedOfferScreen";
import AdminPanel from "./screens/AdminPanel";
import HowItWorksScreen from "./screens/HowItWorksScreen";
import SharedOfferScreen from "./screens/SharedOfferScreen";
import LocationsScreen from "./screens/LocationsScreen";
import ContactScreen from "./screens/ContactScreen";
import AdminLoginScreen from "./screens/AdminLoginScreen";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
export const API = `${BACKEND_URL}/api`;

export const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || "http://localhost:3000";

// Partner-branded Logo Component (falls back to NucarNext by default)
const NucarLogo = ({ light = false, size = "default" }) => {
  if (brandConfig.noLogo) {
    return (
      <div className="flex items-center" data-testid="nucarnext-logo">
        <span className={`font-bold ${size === "small" ? "text-base" : "text-lg"} ${light ? "text-white" : "text-slate-900"}`}>
          {brandConfig.name}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center">
      <img
        src={light ? brandConfig.logoLight : brandConfig.logoDark}
        alt={brandConfig.name}
        className={size === "small" ? "h-6" : "h-8"}
        data-testid="nucarnext-logo"
      />
    </div>
  );
};

// Shared Header Component
export const Header = ({ showProgress = false, currentStep = 0, totalSteps = 7, variant = "light" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const isHome = location.pathname === '/';
  const isDark = variant === "dark" || isHome || isAdmin;
  const { isEmbedded: isWidgetMode, isCompact } = useWidgetMode();

  return (
    <header className={`sticky top-0 z-50 ${isDark ? 'header-nucar' : 'bg-white/95 backdrop-blur-md border-b border-slate-200'}`}>
      <div className={`container-wide flex items-center justify-between ${isCompact ? 'py-2' : 'py-4'}`}>
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => navigate('/')}
          data-testid="logo-link"
        >
          <NucarLogo light={isDark} size={isCompact ? "small" : "default"} />
        </div>

        {showProgress && !isAdmin && !isHome && (
          <div className="flex items-center gap-2">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={`step-${i}`}
                className={`progress-step ${i === currentStep ? 'active' : ''} ${i < currentStep ? 'completed' : ''}`}
              />
            ))}
          </div>
        )}
        
        <nav className="flex items-center gap-6">
          {!isAdmin && !isWidgetMode && (
            <>
              <button 
                onClick={() => navigate('/how-it-works')}
                className={`text-sm font-medium transition-colors ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                data-testid="how-it-works-link"
              >
                How It Works
              </button>
              <button 
                onClick={() => navigate('/locations')}
                className={`text-sm font-medium transition-colors ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                data-testid="locations-link"
              >
                Locations
              </button>
              <button 
                onClick={() => navigate('/contact')}
                className={`text-sm font-medium transition-colors ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                data-testid="contact-link"
              >
                Contact
              </button>
            </>
          )}
          {isAdmin && (
            <button
              onClick={() => navigate('/')}
              className="text-slate-300 hover:text-white text-sm font-medium transition-colors"
              data-testid="exit-admin-link"
            >
              Exit Admin
            </button>
          )}
        </nav>
      </div>
    </header>
  );
};

// Shared Footer Component
export const Footer = ({ minimal = false }) => {
  const navigate = useNavigate();
  
  if (minimal) {
    return (
      <footer className="py-6 border-t border-slate-200 bg-white">
        <div className="container-wide text-center">
          <p className="text-sm text-slate-500">
            © 2026 {brandConfig.name}. All rights reserved.
          </p>
        </div>
      </footer>
    );
  }
  
  return (
    <footer className="footer-nucar py-16">
      <div className="container-wide">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <NucarLogo light />
            <p className="text-slate-400 mt-4 text-sm leading-relaxed">
              The smarter way to sell, trade, or understand your vehicle's true value.
            </p>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><button onClick={() => navigate('/')} className="footer-link text-sm">Get Your Offer</button></li>
              <li><button onClick={() => navigate('/how-it-works')} className="footer-link text-sm">How It Works</button></li>
            </ul>
          </div>
          
          {/* Support */}
          <div>
            <h4 className="text-white font-semibold mb-4">Support</h4>
            <ul className="space-y-2">
              <li><button onClick={() => navigate('/how-it-works')} className="footer-link text-sm">FAQ</button></li>
              <li><button onClick={() => navigate('/contact')} className="footer-link text-sm">Contact Us</button></li>
              <li><button onClick={() => navigate('/locations')} className="footer-link text-sm">Locations</button></li>
            </ul>
          </div>
          
          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Backed by 32 Nucar Stores</h4>
            <p className="text-slate-400 text-sm">
              Dealerships across New England, Delaware, and Pennsylvania ready to serve you.
            </p>
            <p className="text-slate-400 text-sm mt-2">
              Buy Center: <span className="text-white font-medium">1-800-781-NUCAR</span>
            </p>
          </div>
        </div>
        
        <div className="pt-8 border-t border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © 2026 {brandConfig.name}. All rights reserved.
          </p>
          <p className="text-slate-500 text-sm">
            No obligation • Instant results • Free forever
          </p>
        </div>
      </div>
    </footer>
  );
};

// App State Context
// initialOverrides lets a widget mount start pre-populated (eg. the bar-mode
// -> modal handoff below, which mounts the modal already on the vehicle
// -confirm screen with a vehicle already decoded, instead of blank at "/").
export const useAppState = (initialOverrides = null) => {
  const [state, setState] = useState({
    vin: '',
    vehicleInfo: null,
    mileage: '',
    zipCode: '',
    offer: null,
    conditionAnswers: [],
    photos: [],
    lockedOffer: null,
    ...initialOverrides
  });

  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const resetState = useCallback(() => {
    setState({
      vin: '',
      vehicleInfo: null,
      mileage: '',
      zipCode: '',
      offer: null,
      conditionAnswers: [],
      photos: [],
      lockedOffer: null
    });
  }, []);

  return { state, updateState, resetState };
};

function AppContent({ initialVehicle = null, onVehicleDecoded = null }) {
  const { state, updateState, resetState } = useAppState(
    initialVehicle ? { vin: initialVehicle.vin, vehicleInfo: initialVehicle } : null
  );
  const location = useLocation();
  const containerRef = useRef(null);
  const { isCompact, isBar } = useWidgetMode();
  // min-h-screen is a *page* concern — it keeps the standalone site's footer
  // pinned to the bottom on short screens. The small-footprint embed modes
  // (compact, bar) sit inside a host page's own layout and must be exactly
  // as tall as their content; leaving min-h-screen on them makes the widget
  // claim a full viewport of vertical space on a real dealer page.
  const fitsContent = isCompact || isBar;

  // Scroll to the top of the widget itself (not window.scrollTo, which would
  // yank the whole host page) on every screen change, so users who scrolled
  // down to hit Continue land at the top of the next screen instead of
  // wherever they happened to be. Skipped on the *initial* render: nobody has
  // navigated yet, and scrollIntoView on mount does yank the host page —
  // an embed below the fold (eg. a bar under a VDP's photo) scrolled the
  // dealer's page to itself as soon as the widget booted.
  // Compared against the last *path* rather than a "have I run yet" flag so
  // StrictMode's dev-only double-invoke doesn't sneak a mount scroll back in.
  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    if (lastPathRef.current === location.pathname) return;
    lastPathRef.current = location.pathname;
    containerRef.current?.scrollIntoView({ block: "start" });
  }, [location.pathname]);

  return (
    <div ref={containerRef} className={fitsContent ? "bg-slate-50" : "min-h-screen bg-slate-50"}>
      <Routes>
        <Route 
          path="/" 
          element={
            <VINEntryScreen
              state={state}
              updateState={updateState}
              resetState={resetState}
              onVehicleDecoded={onVehicleDecoded}
            />
          }
        />
        <Route
          path="/confirm-vehicle"
          element={
            <VehicleConfirmScreen 
              state={state} 
              updateState={updateState} 
            />
          } 
        />
        <Route 
          path="/mileage-zip" 
          element={
            <MileageZipScreen 
              state={state} 
              updateState={updateState} 
            />
          } 
        />
        <Route 
          path="/instant-offer" 
          element={
            <InstantOfferScreen 
              state={state} 
              updateState={updateState} 
            />
          } 
        />
        <Route 
          path="/condition" 
          element={
            <ConditionScreen 
              state={state} 
              updateState={updateState} 
            />
          } 
        />
        <Route 
          path="/photos" 
          element={
            <PhotoUploadScreen 
              state={state} 
              updateState={updateState} 
            />
          } 
        />
        <Route 
          path="/locked-offer" 
          element={
            <LockedOfferScreen 
              state={state} 
              resetState={resetState}
            />
          } 
        />
        <Route path="/how-it-works" element={<HowItWorksScreen />} />
        <Route path="/locations" element={<LocationsScreen />} />
        <Route path="/contact" element={<ContactScreen />} />
        <Route path="/offer/view/:lockedOfferId" element={<SharedOfferScreen />} />
        <Route path="/admin/login" element={<AdminLoginScreen />} />
        <Route path="/admin/*" element={<AdminPanel />} />
      </Routes>
      <Toaster position="top-center" richColors />
    </div>
  );
}

// Embedded widget mode uses MemoryRouter — the host page's own URL belongs
// to the host, not to us, and a host page not served at exactly one of our
// route paths (very common: any real dealer embed, any non-root test page)
// would otherwise make BrowserRouter match nothing and render blank. The
// standalone site keeps BrowserRouter, since /offer/view/:id sharing and
// normal back-button/refresh behavior genuinely need the real address bar.
function App({
  isEmbeddedWidget = false,
  compact = false,
  bar = false,
  initialVehicle = null,
  onVehicleDecoded = null,
  shadowContainer = null,
}) {
  const RouterImpl = isEmbeddedWidget ? MemoryRouter : BrowserRouter;
  // A pre-decoded vehicle (bar-mode -> modal handoff, see index.js) skips
  // straight to the vehicle-confirm screen instead of mounting blank at "/".
  const routerProps = isEmbeddedWidget && initialVehicle ? { initialEntries: ["/confirm-vehicle"] } : {};
  // Scoped to this <App> tree via context, not a shared singleton — a page
  // can have more than one widget instance mounted at once (eg. the bar +
  // modal handoff pattern in widget-helpers.js), and each needs its own
  // independent mode/portal-target rather than clobbering the others.
  const widgetModeValue = { isEmbedded: isEmbeddedWidget, isCompact: compact, isBar: bar };
  return (
    <WidgetModeProvider value={widgetModeValue}>
      <ShadowContainerProvider value={shadowContainer}>
        <RouterImpl {...routerProps}>
          <AppContent initialVehicle={initialVehicle} onVehicleDecoded={onVehicleDecoded} />
        </RouterImpl>
      </ShadowContainerProvider>
    </WidgetModeProvider>
  );
}

export default App;
